import React, { useCallback } from 'react';
import {
  Alert,
  AlertDescription,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { PaidPayrollTab } from '../components/payroll/PaidPayrollTab';
import { UnpaidPayrollTab } from '../components/payroll/UnpaidPayrollTab';
import { usePayrollAccess } from '../components/payroll/usePayrollAccess';

type PayrollView = 'unpaid' | 'paid';

const VIEW_HEADINGS: Record<
  PayrollView,
  { title: string; description: string }
> = {
  unpaid: {
    title: 'Unpaid Visits',
    description: 'Completed visits not yet included in a payroll',
  },
  paid: {
    title: 'Paid Payrolls',
    description: 'Payrolls already created, by payroll date',
  },
};

/** Payroll Manager shell: Unpaid / Paid tabs (Billing Manager pattern). */
const PayrollManager: React.FC = () => {
  // Same rule the sidebar entry gates on, so a bookmarked or typed-in URL
  // can't render the screen for someone the API would refuse anyway.
  const hasAccess = usePayrollAccess();

  // Kept in the URL so the detail screen can navigate back to the Paid tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView: PayrollView =
    searchParams.get('tab') === 'paid' ? 'paid' : 'unpaid';

  const handleViewChange = useCallback(
    (value: string) => {
      setSearchParams(value === 'paid' ? { tab: 'paid' } : {}, {
        replace: true,
      });
    },
    [setSearchParams]
  );

  if (!hasAccess) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex items-center gap-2'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don&apos;t have permission to access Payroll Manager. Please
            contact your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <Tabs
        value={activeView}
        onValueChange={handleViewChange}
        className='flex w-full flex-col gap-6'
        variant='purple'
      >
        <div className='flex flex-wrap items-start justify-between gap-4'>
          <div>
            <h1 className='text-2xl font-bold tracking-tight'>
              {VIEW_HEADINGS[activeView].title}
            </h1>
            <p className='text-muted-foreground text-sm'>
              {VIEW_HEADINGS[activeView].description}
            </p>
          </div>
          <TabsList>
            <TabsTrigger value='unpaid'>Unpaid</TabsTrigger>
            <TabsTrigger value='paid'>Paid</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value='unpaid' className='m-0'>
          <UnpaidPayrollTab />
        </TabsContent>

        <TabsContent value='paid' className='m-0'>
          <PaidPayrollTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PayrollManager;
